import React, { useCallback, useRef } from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import * as yup from 'yup';
import FileInput from '../Inputs/FileInput';
import FileGridList from '../Inputs/File/FileGridList';
import { convertBytesToMegaBytes } from '../../Utils/Calculations/file';
import BaseTooltip from '../Tooltip/BaseTooltip';
import { VALID_FILE_EXTENSIONS } from '../../Utils/enums';

const getFileSchema = validFileExtensions =>
  yup.object({
    size: yup
      .number()
      // eslint-disable-next-line no-template-curly-in-string
      .test('fileSize', '${path}->Tamaño máximo: 10 MB', value => {
        return value <= 10240000;
      }),
    name: yup.string(),
    type: yup.string(),
    attachemntType: yup.string(),
    extension: yup
      .string()
      // eslint-disable-next-line no-template-curly-in-string
      .test('extension', '${path}->Archivo no permitido', extension =>
        validFileExtensions.includes(extension)
      ),
    file: yup.mixed()
  });

const parseFile = (
  file,
  validFileExtensions = VALID_FILE_EXTENSIONS,
  attachmentType = null
) => {
  const { name, size, type } = file;

  const extension = name
    .split('.')
    .pop()
    .toLowerCase();

  const preparedFile = {
    name,
    attachmentType,
    size,
    type,
    extension,
    file,
    error: false
  };

  try {
    const fileSchema = getFileSchema(validFileExtensions);

    fileSchema.validateSync(preparedFile, {
      abortEarly: false
    });
  } catch (err) {
    for (const errorMessage of err.errors) {
      const message = errorMessage.split('->')[1];

      preparedFile.error = true;
      preparedFile.errorMessage = message;
    }
  }

  return preparedFile;
};

const AttachmentInput = ({
  index = 0,
  label,
  helperText,
  required,
  validFileExtensions = VALID_FILE_EXTENSIONS,
  templateLink = null,
  attachmentType = null,
  fileInputId,
  files,
  totalSize,
  setFiles,
  setTotalSize,
  maxMBFileSize
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const handleChangeFile = useCallback(
    e => {
      const inputFile = Array.from(e.target.files)[0];

      const parsedFile = parseFile(
        inputFile,
        validFileExtensions,
        attachmentType
      );

      const updatedFiles = files.slice();
      updatedFiles[index] = parsedFile;

      const totalFilesSize = updatedFiles
        .filter(file => file)
        .reduce((total, file) => total + file.size, 0);

      setFiles(updatedFiles);
      setTotalSize(convertBytesToMegaBytes(totalFilesSize));
    },
    [files, attachmentType, validFileExtensions, setFiles, setTotalSize, index]
  );

  const handleDeleteFile = useCallback(() => {
    const updatedFiles = files.map((file, i) => {
      if (i === index) {
        return null;
      }
      return file;
    });

    const totalFilesSize = updatedFiles
      .filter(file => file)
      .reduce((total, file) => total + file.size, 0);

    setFiles(updatedFiles);
    setTotalSize(convertBytesToMegaBytes(totalFilesSize));
    fileInputRef.current.value = '';
  }, [files, index, setFiles, setTotalSize, fileInputRef]);

  const renderLabel = () => (
    <Typography className={classes.label}>
      {label} {required && '* '}
      {templateLink && (
        <a href={templateLink} download>
          (Descargar plantilla)
        </a>
      )}
    </Typography>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {helperText && (
          <BaseTooltip
            tooltipText={helperText}
            text={renderLabel()}
            textClass={classes.label}
          />
        )}
        {!helperText && renderLabel()}
      </Grid>

      <Grid item xs="auto">
        <FileInput
          id={`${fileInputId}_${index}`}
          ref={fileInputRef}
          name={`attachments.${index}`}
          accept={validFileExtensions.map(ext => `.${ext}`).join(',')}
          text="Adjuntar archivo"
          helperText={
            files[index] === null && required && 'Este documento es requerido'
          }
          inputProps={{
            onChange: handleChangeFile
          }}
          error={
            (files[index] && files[index].error) ||
            totalSize > maxMBFileSize ||
            (required && files[index] === null)
          }
          disabled={totalSize > maxMBFileSize}
          buttonProps={{
            color: 'default'
          }}
          iconVisible
        />
      </Grid>
      {files && files[index] && (
        <FileGridList
          id={`Pqr_detail_filelist_${index}`}
          files={files.slice(index, index + 1)}
          onDeleteFile={handleDeleteFile}
          className={classes.input}
          xs={9}
        />
      )}
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  label: {
    fontSize: 14
  },
  input: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing()
    }
  }
}));

export default AttachmentInput;
